// ─── POI Data Hook ──────────────────────────────────────────────────────
// Owns all POI-related state: global POI list, per-stop nearby POIs,
// connection lines for the map, and the discovery score.
//
// Extracted from TripContext so POI updates don't trigger re-renders in
// components that only care about trip state (and vice versa).

import { useState, useEffect, useRef, useMemo } from "react";
import type { City } from "../data/types";
import { getNearbyPOIs, getAllPOIs, type POI } from "../services/poiService";
import { reportError } from "../services/errorService";

export interface POIData {
  allPOIs: POI[];
  tripPOIs: POI[];
  poiConnections: Array<{ from: [number, number]; to: [number, number]; poi: POI }>;
  discoveryValue: number;
}

type StopConns = Array<{ from: [number, number]; to: [number, number]; poi: POI }>;

async function loadStopConnections(stop: City): Promise<{ id: string; conns: StopConns }> {
  try {
    const pois = await getNearbyPOIs(stop.latitude, stop.longitude, 75, 6);
    const conns: StopConns = pois.map(poi => ({
      from: [stop.latitude, stop.longitude], to: [poi.latitude, poi.longitude], poi,
    }));
    return { id: stop.id, conns };
  } catch (error) {
    reportError(error, { level: "info", context: { op: "getNearbyPOIs", stopId: stop.id } });
    return { id: stop.id, conns: [] };
  }
}

export function usePOIData(tripStops: City[]): POIData {
  const [allPOIs, setAllPOIs] = useState<POI[]>([]);
  const [poiConnections, setPoiConnections] = useState<StopConns>([]);
  const [discoveryValue, setDiscoveryValue] = useState(0);

  // ─── Load all POIs on mount ───────────────────────────────────
  useEffect(() => {
    getAllPOIs()
      .then((pois) => {
        if (import.meta.env.DEV) console.log(`[usePOIData] Loaded ${pois.length} POIs`);
        setAllPOIs(pois);
      })
      .catch((err) => {
        reportError(err, { level: "info", context: { op: "getAllPOIs" } });
      });
  }, []);

  // ─── POI connections from trip stops ──────────────────────────
  // Cache per-stop results so adding/removing one stop only fetches
  // POIs for the new stop, not all stops.
  const poiCacheRef = useRef<Map<string, StopConns>>(new Map());

  useEffect(() => {
    if (tripStops.length === 0) {
      poiCacheRef.current.clear();
      setPoiConnections([]);
      setDiscoveryValue(0);
      return;
    }

    const currentIds = new Set(tripStops.map((s) => s.id));
    const cachedIds = new Set(poiCacheRef.current.keys());

    // Drop removed stops from cache
    for (const id of cachedIds) {
      if (!currentIds.has(id)) poiCacheRef.current.delete(id);
    }

    // Find stops we haven't fetched yet
    const newStops = tripStops.filter(
      (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude) && !poiCacheRef.current.has(s.id)
    );

    if (newStops.length === 0) {
      // Nothing new — just rebuild from cache (handles removals)
      rebuildFromCache();
      return;
    }

    let cancelled = false;

    Promise.all(newStops.map(loadStopConnections)).then((results) => {
      if (cancelled) return;
      for (const { id, conns } of results) {
        poiCacheRef.current.set(id, conns);
      }
      rebuildFromCache();
    });

    return () => { cancelled = true; };

    function rebuildFromCache() {
      const seen = new Set<string>();
      const all: StopConns = [];
      for (const conns of poiCacheRef.current.values()) {
        for (const conn of conns) {
          const key = conn.poi.id ?? `${conn.poi.latitude},${conn.poi.longitude}`;
          if (!seen.has(key)) {
            seen.add(key);
            all.push(conn);
          }
        }
      }
      setPoiConnections(all);
    }
  }, [tripStops]);

  // ─── Unique POIs near trip stops (for map markers) ────────────
  const tripPOIs = useMemo(() => {
    const seen = new Set<string>();
    const pois: POI[] = [];
    for (const conn of poiConnections) {
      const key = conn.poi.id ?? `${conn.poi.latitude},${conn.poi.longitude}`;
      if (!seen.has(key)) {
        seen.add(key);
        pois.push(conn.poi);
      }
    }
    return pois;
  }, [poiConnections]);

  // ─── Discovery score from actual trip stops ───────────────────
  useEffect(() => {
    let score = 0;
    for (const s of tripStops) {
      const t = s.poiType;
      if (!t)                    score += 5;   // plain city / town
      else if (t === "hidden-gem") score += 20;  // hidden gem
      else                       score += 10;  // any other POI
    }
    setDiscoveryValue(score);
  }, [tripStops]);

  return { allPOIs, tripPOIs, poiConnections, discoveryValue };
}
